import { Input, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { adminUserService, type AdminAgencyUser, type AgencySubrole } from "@/api/user";
import { CampaignStyledTable } from "@/features/campaign";
import { AppSelect, Icon } from "@/shared/components";
import type { CustomTableColumn } from "@/shared/components/Table";
import { useColumnVisibility } from "../hooks/useColumnVisibility";
import { toColumnSpecs } from "../utils/columnVisibility";
import { ColumnSettings, ScrollTable } from "./ColumnSettings";

const Toolbar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 24px;
`;

const Search = styled(Input)`
	width: 280px;
	&& {
		height: 40px;
		border-color: #cdd4f6;
		border-radius: 8px;
		font-size: 16px;
	}
`;

const RoleSelect = styled(AppSelect)`
	width: 180px;
	&& .ant-select-selector {
		height: 40px;
		align-items: center;
		border-color: #cdd4f6;
		border-radius: 8px;
	}
`;

export const AgencyUserSubroles = () => {
	const [users, setUsers] = useState<AdminAgencyUser[]>([]);
	const [loading, setLoading] = useState(true);
	const [query, setQuery] = useState("");
	const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
	useEffect(() => {
		adminUserService
			.listAgencyUsers()
			.then(setUsers)
			.catch((error: any) => message.error(error?.response?.data?.message || "Unable to load agency users"))
			.finally(() => setLoading(false));
	}, []);

	const updateSubrole = async (userId: string, agencySubrole: AgencySubrole) => {
		const previous = users.find((user) => user.uuid === userId)?.agencySubrole;
		if (!previous || previous === agencySubrole || updatingIds.has(userId)) return;
		setUsers((current) => current.map((user) => (user.uuid === userId ? { ...user, agencySubrole } : user)));
		setUpdatingIds((current) => new Set(current).add(userId));
		try {
			await adminUserService.updateAgencySubrole(userId, agencySubrole);
			message.success("Agency subrole updated");
		} catch (error: any) {
			setUsers((current) =>
				current.map((user) => (user.uuid === userId ? { ...user, agencySubrole: previous } : user)),
			);
			message.error(error?.response?.data?.message || "Unable to update agency subrole");
		} finally {
			setUpdatingIds((current) => {
				const next = new Set(current);
				next.delete(userId);
				return next;
			});
		}
	};

	const filteredUsers = useMemo(() => {
		const value = query.trim().toLowerCase();
		return value ? users.filter((user) => `${user.name} ${user.email}`.toLowerCase().includes(value)) : users;
	}, [query, users]);

	const columns: CustomTableColumn<AdminAgencyUser>[] = [
		{ title: "Name", dataIndex: "name", key: "name", width: 260, enableSort: true, enableFilter: true },
		{ title: "Email", dataIndex: "email", key: "email", width: 300, enableSort: true },
		{ title: "Account type", key: "accountType", width: 160, render: () => "Agency" },
		{
			title: "Subrole",
			key: "agencySubrole",
			width: 220,
			enableFilter: true,
			render: (_, user) => (
				<RoleSelect
					aria-label={`Subrole for ${user.name}`}
					value={user.agencySubrole}
					disabled={updatingIds.has(user.uuid)}
					options={[
						{ value: "standard", label: "Standard" },
						{ value: "finance", label: "Finance" },
					]}
					onChange={(value) => void updateSubrole(user.uuid, value as AgencySubrole)}
				/>
			),
		},
	];

	const columnSettings = useColumnVisibility("agency-users", toColumnSpecs(columns));

	return (
		<>
			<Toolbar>
				<Search
					aria-label="Search agency users"
					placeholder="Search agency users..."
					suffix={<Icon src="/icon/menu_search.svg" size={22} />}
					value={query}
					onChange={(event) => setQuery(event.target.value)}
				/>
				<ColumnSettings
					columns={toColumnSpecs(columns)}
					hidden={columnSettings.hidden}
					onToggle={columnSettings.toggle}
					onReset={columnSettings.reset}
				/>
			</Toolbar>
			<ScrollTable $minWidth={columnSettings.minWidth}>
				<CampaignStyledTable<AdminAgencyUser>
					rowKey="uuid"
					columns={columns.filter((column) => !columnSettings.hidden.includes(String(column.key)))}
					dataSource={filteredUsers}
					loading={loading}
					pagination={false}
				/>
			</ScrollTable>
		</>
	);
};
